import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, ClipboardList, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { brl, pct } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useLayout } from "@/context/LayoutContext";
import { Card, Button, EmptyState, Loading, ErrorState, Page, Segmented } from "@/components/kit";
import { WeeklyComparison, PriceAlerts } from "@/components/DashboardWidgets";
import { LastWeekCard, Kpi, EvolutionChart, CategoryBreakdown, WeeklyHistory, periodTotals } from "@/components/DashboardParts";
import MyTasks from "@/components/tasks/MyTasks";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";

const RANGES = [{ value: "4", label: "Últimas 4", n: 4 }, { value: "12", label: "Últimas 12", n: 12 }, { value: "all", label: "Tudo", n: 9999 }];
const rangeSize = (range) => RANGES.find((r) => r.value === range).n;

export function TargetAlert({ value, target, testid = "cmv-target-alert" }) {
  if (value === null || value === undefined || value <= target) return null;
  return (
    <div className="flex items-center gap-3 rounded-xl p-3" style={{ background: "var(--accent-soft)" }} data-testid={testid}>
      <AlertTriangle size={18} style={{ color: "var(--accent)" }} />
      <span className="label flex-1">CMV de {pct(value)} acima da meta de {pct(target, 0)} (+{pct(value - target)})</span>
    </div>
  );
}

function OperatorHome() {
  const nav = useNavigate();
  return (
    <Card className="max-w-xl" data-testid="operator-info-card">
      <ClipboardList size={26} style={{ color: "var(--brand)" }} />
      <div className="h2 mt-3">Conferências</div>
      <p className="muted mt-2">Seu perfil de Operador registra as contagens de estoque. Os relatórios de CMV ficam disponíveis para gerentes e administradores.</p>
      <Button className="mt-5" onClick={() => nav("/conferences")} data-testid="go-conferences-button">Ir para conferências</Button>
    </Card>
  );
}

function NoData() {
  const nav = useNavigate();
  return (
    <EmptyState title="Nenhuma conferência ainda" text="Cadastre seus produtos e registre a primeira conferência para ver o CMV."
      action={<Button icon={Plus} onClick={() => nav("/conferences/new")} data-testid="empty-new-conference-button">Nova conferência</Button>} />
  );
}

function MgmtDashboard() {
  const { layout } = useLayout();
  const show = layout?.sections || {};
  const [range, setRange] = useState("12");
  const { data, isLoading, isError, error, refetch } = useQuery({ queryKey: ["summary"], queryFn: () => api("/reports/summary") });
  const weeks = useMemo(() => (data?.weeks || []).slice(-rangeSize(range)), [data, range]);
  const totals = useMemo(() => periodTotals(weeks), [weeks]);

  if (isLoading) return <Loading />;
  if (isError) return <ErrorState message={error.message} onRetry={refetch} />;
  if (!data.last) return <NoData />;
  const target = data.target;
  return (
    <div className="flex flex-col gap-5">
      <TargetAlert value={data.last.pct} target={target} testid="dashboard-target-alert" />
      <div className="grid lg:grid-cols-3 gap-5">
        <LastWeekCard last={data.last} target={target} />
        <div className="lg:col-span-2 flex flex-col gap-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="h3">Período</div>
            <Segmented options={RANGES} value={range} onChange={setRange} testid="range" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Kpi label="Faturamento" value={brl(totals.rev)} testid="period-revenue-card" />
            <Kpi label="CMV" value={brl(totals.cmv)} testid="period-cmv-card" />
            <Kpi label="CMV médio" value={pct(totals.pct)} testid="period-cmv-pct-card" />
          </div>
          {show.chart !== false && <EvolutionChart weeks={weeks} target={target} />}
        </div>
      </div>
      <div className="grid lg:grid-cols-2 gap-5">
        {show.categories !== false && <CategoryBreakdown cats={totals.cats} />}
        {show.price_alerts !== false && <PriceAlerts />}
      </div>
      {show.comparison !== false && <WeeklyComparison />}
      {show.history !== false && <WeeklyHistory weeks={weeks} target={target} />}
    </div>
  );
}

export default function Dashboard() {
  const { user, isMgmt } = useAuth();
  return (
    <Page>
      <div className="mb-6 fade-in">
        <div className="caption">Olá, {user?.name?.split(" ")[0]} · {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}</div>
        <h1 className="h1 mt-1">Painel de CMV</h1>
      </div>
      <OnboardingChecklist />
      <MyTasks />
      {isMgmt ? <MgmtDashboard /> : <OperatorHome />}
    </Page>
  );
}
